import { TwoStepCard } from '@/features/auth/two-step-card';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrade } from '@/lib/trade';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError, request, tokenStore } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import i18n from '@/lib/i18n';
import { userRoleSchema, type UserRole } from '@/lib/schemas';

// התוויות המשותפות (common) — אותן תוויות כמו בסרגל הצד.
const ROLE_LABEL_KEY = {
  OWNER: 'common:roles.OWNER',
  MANAGER: 'common:roles.MANAGER',
  VIEWER: 'common:roles.VIEWER',
  FIELD: 'common:roles.FIELD',
} as const satisfies Record<UserRole, string>;

/**
 * מראה את `ChangePasswordDto` בשרת אות באות:
 * `PASSWORD_RULE = /^(?=.*[A-Za-z])(?=.*\d).{12,128}$/`.
 * האורך הוא ההגנה האמיתית; דרישת האות והספרה רק חוסמת
 * "123456789012". כלל מחמיר יותר בקליינט היה דוחה סיסמאות שהשרת
 * מקבל — ופחות מחמיר היה מייצר 400 אחרי לחיצה.
 */
const PASSWORD_RULE = /^(?=.*[A-Za-z])(?=.*\d).{12,128}$/;

const schema = z
  .object({
    currentPassword: z.string().min(1, i18n.t('settings:profile.currentRequired')).max(128),
    newPassword: z
      .string()
      .regex(PASSWORD_RULE, i18n.t('settings:profile.passwordRule')),
    confirmPassword: z.string().min(1, i18n.t('settings:profile.confirmRequired')),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    path: ['confirmPassword'],
    message: i18n.t('settings:profile.mismatch'),
  });

type FormValues = z.infer<typeof schema>;

/** תשובת `POST /v1/auth/change-password` — `AuthResponse` בשרת. */
const authResponseSchema = z.object({
  accessToken: z.string(),
  mustChangePassword: z.boolean(),
  user: z.object({
    id: z.string().uuid(),
    name: z.string(),
    email: z.string(),
    role: userRoleSchema,
  }),
});

export function ProfilePanel() {
  const { t } = useTranslation(['settings', 'common']);
  const trade = useTrade();
  const roleLabel = (r: UserRole) => (r === 'FIELD' ? trade.worker.one : t(ROLE_LABEL_KEY[r]));
  const { session } = useAuth();
  const toast = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const changePassword = useMutation({
    mutationFn: (values: FormValues) =>
      request('/auth/change-password', {
        method: 'POST',
        // בדיוק שני השדות של ה-DTO: ה-ValidationPipe הגלובלי מוגדר
        // forbidNonWhitelisted, ו-confirmPassword היה מחזיר 400.
        body: { currentPassword: values.currentPassword, newPassword: values.newPassword },
        schema: authResponseSchema,
      }),
    onSuccess: (res) => {
      // השרת מנפיק טוקן חדש בשינוי סיסמה. בלי לשמור אותו, הסשן
      // ממשיך עם טוקן שנחתם לפני השינוי.
      tokenStore.set(res.accessToken);
      reset();
      toast.success(t('profile.updated'));
    },
    onError: (err: unknown) => {
      toast.error(t('profile.updateFailed'), err instanceof ApiError ? err.message : t('common:errors.generic'));
    },
  });

  const onSubmit = handleSubmit((values) => changePassword.mutateAsync(values).catch(() => {}));

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle>{t('profile.userTitle')}</CardTitle>
          <CardDescription>{t('profile.userDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Row label={t('profile.name')}>{session?.user.name ?? '—'}</Row>
          <Row label={t('profile.email')}>
            <span className="ltr-inline">{session?.user.email ?? '—'}</span>
          </Row>
          <Row label={t('profile.role')}>
            {session ? <Badge tone="accent">{roleLabel(session.user.role)}</Badge> : '—'}
          </Row>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('profile.passwordTitle')}</CardTitle>
          <CardDescription>{t('profile.passwordDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => void onSubmit(e)} noValidate className="max-w-sm space-y-4">
            <Field
              label={t('profile.current')}
              htmlFor="current-password"
              required
              error={errors.currentPassword?.message}
            >
              <Input
                id="current-password"
                type="password"
                autoComplete="current-password"
                invalid={Boolean(errors.currentPassword)}
                {...register('currentPassword')}
              />
            </Field>

            <Field
              label={t('profile.new')}
              htmlFor="new-password"
              required
              hint={t('profile.newHint')}
              error={errors.newPassword?.message}
            >
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                invalid={Boolean(errors.newPassword)}
                {...register('newPassword')}
              />
            </Field>

            <Field
              label={t('profile.confirm')}
              htmlFor="confirm-password"
              required
              error={errors.confirmPassword?.message}
            >
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                invalid={Boolean(errors.confirmPassword)}
                {...register('confirmPassword')}
              />
            </Field>

            <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
              {t('profile.submit')}
            </Button>
          </form>
        </CardContent>
      </Card>
      <TwoStepCard />
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
      <span className="text-fg-muted">{label}</span>
      <span className="font-medium text-fg">{children}</span>
    </div>
  );
}
